import type { Locale } from "next/router";

import { TFunction } from "i18next";

import {
  Country,
  GetProductShippingDetailsQuery_getProductShippingDetailsV2_ProductShippingDetailsV2_shippingOptions_ProductShippingOptionV2,
  ShippingMethodEnum,
} from "@/Apollo/schema";
import { formatPrice } from "@/common/utils/intl.utils";
import { objectKeys } from "@/common/utils/object.utils";

type GetCountryCodeFromStringUnsupported = {
  supported: false;
  code?: string;
};

type GetCountryCodeFromStringSupported = {
  supported: true;
  code: Country;
};

export type GetCountryCodeFromString =
  | GetCountryCodeFromStringUnsupported
  | GetCountryCodeFromStringSupported;

const createUnsupportedCountruResponse = (
  countryCode?: string | null
): GetCountryCodeFromStringUnsupported => ({
  supported: false,
  code: countryCode ?? undefined,
});

export const getCountryCodeFromString = (
  countryCode?: string | null
): GetCountryCodeFromString => {
  if (!countryCode) {
    return createUnsupportedCountruResponse();
  }

  const upperCode = countryCode.toUpperCase();

  const keys = objectKeys(Country);
  const enumKey = keys.find(key => Country[key] === upperCode);

  if (!enumKey) {
    return createUnsupportedCountruResponse(upperCode);
  }

  const code = Country[enumKey];

  if (!code) {
    return createUnsupportedCountruResponse(upperCode);
  }

  return {
    supported: true,
    code,
  };
};

export const isSupportedCountryCode = (
  input?: GetCountryCodeFromString
): input is GetCountryCodeFromStringSupported => {
  return input?.supported === true;
};

export type DeliveryPriceLabel = {
  label: string;
  isFree: boolean;
};

/**
 * Formats the price shown on a delivery option row. Variable-rate options render
 * as "from €X" (the amount is the lowest available rate); custom shipping is a
 * fixed price set by the seller so it renders the plain amount; free options
 * render the free label.
 */
export const getProductDeliveryPriceLabel = (
  option: GetProductShippingDetailsQuery_getProductShippingDetailsV2_ProductShippingDetailsV2_shippingOptions_ProductShippingOptionV2,
  t: TFunction,
  locale: Locale
): DeliveryPriceLabel => {
  const amount = option.price?.amount;

  if (!amount) {
    return { label: t("common:common-free"), isFree: true };
  }

  const price = formatPrice(amount, { locale });
  const isCustom = option.shippingMethod === ShippingMethodEnum.Custom;

  return {
    label: isCustom
      ? price
      : `${t("common:common-from").toLowerCase()} ${price}`,
    isFree: false,
  };
};
